/**
 * Every event a workflow can subscribe to, in one place.
 *
 * This is the list the builder's dropdown renders *and* the list the schema's
 * picklists validate against. Keeping them the same object is the point: an
 * event the UI offers but the schema rejects is a save that fails after the
 * user has already chosen, and an event the schema accepts but the UI hides is
 * a feature only reachable by hand-editing JSON.
 *
 * Adding an event here is half the work. The other half is teaching the
 * source's `classify*` function to emit it — see `apps/api/src/triggers/`.
 */

export interface TriggerEventOption {
  /** The value stored in the workflow document. */
  value: string;
  /** What the dropdown shows. */
  label: string;
  /** One line of "when does this actually fire", for the option's help text. */
  description: string;
}

export const LINEAR_TRIGGER_EVENTS = [
  {
    value: 'issue.created',
    label: 'Issue created',
    description: 'A new issue is created in a subscribed team or project.',
  },
  {
    value: 'issue.updated',
    label: 'Issue updated',
    description: 'Any field on an issue changes — the broadest Linear event.',
  },
  {
    value: 'issue.labeled',
    label: 'Issue labelled',
    description: "An issue's labels change. Narrow it with a label filter.",
  },
  {
    value: 'issue.state_changed',
    label: 'Issue state changed',
    description: 'An issue moves between states, e.g. into Triage.',
  },
] as const satisfies readonly TriggerEventOption[];

export const GITHUB_TRIGGER_EVENTS = [
  {
    value: 'pull_request.opened',
    label: 'Pull request opened',
    description: 'A pull request is opened or reopened.',
  },
  {
    value: 'pull_request.closed',
    label: 'Pull request closed',
    description: 'A pull request is closed, whether or not it merged.',
  },
  {
    value: 'pull_request.labeled',
    label: 'Pull request labelled',
    description: 'A label is added to or removed from a pull request.',
  },
  {
    value: 'pull_request.review_submitted',
    label: 'Review submitted',
    description: 'Someone submits a review — approval, changes, or a comment.',
  },
  {
    value: 'pull_request.commented',
    label: 'Pull request commented',
    description:
      'Someone comments on a pull request — in the conversation or on a line of the diff.',
  },
  {
    value: 'pull_request.mentioned',
    label: 'Reef mentioned on a pull request',
    description:
      'A comment, review body or line comment addresses @reef. The comment is the instruction.',
  },
  {
    value: 'issue.assigned',
    label: 'Issue assigned',
    description:
      'An issue is assigned to someone. Narrow it with an assignee filter — assigning to the reef account is how a person hands work over.',
  },
  {
    value: 'push',
    label: 'Push',
    description: 'Commits are pushed. Narrow it with a branch filter.',
  },
  {
    value: 'check_suite.completed',
    label: 'Checks finished',
    description:
      'A check suite finishes, green or red. Narrow it with a payload condition on `check_suite.conclusion` — subscribing to every green run as well is spend for nothing.',
  },
] as const satisfies readonly TriggerEventOption[];

/**
 * Jira's four, named the way reef names Linear's rather than the way Jira names
 * its own.
 *
 * Jira's wire vocabulary is `jira:issue_updated` plus an
 * `issue_event_type_name` of `issue_assigned`, which is two fields describing
 * one fact and neither of them is what a workflow author is thinking about.
 * Collapsing them here means a team that already has a Linear trigger can read
 * a Jira one without learning a second vocabulary — `issue.assigned` is the
 * same sentence in both — and it keeps the provider's shape in the classifier
 * where the rest of its quirks already live.
 */
export const JIRA_TRIGGER_EVENTS = [
  {
    value: 'issue.created',
    label: 'Issue created',
    description: 'A new issue is created in a subscribed project.',
  },
  {
    value: 'issue.updated',
    label: 'Issue updated',
    description: 'Any field on an issue changes — the broadest Jira event.',
  },
  {
    value: 'issue.assigned',
    label: 'Issue assigned',
    description:
      'The assignee changes. Narrow it with an assignee filter — assigning to the reef account is how a person hands work over.',
  },
  {
    value: 'issue.transitioned',
    label: 'Issue transitioned',
    description: "The issue's status changes, e.g. into In Progress. Linear calls this a state change.",
  },
] as const satisfies readonly TriggerEventOption[];

export const SLACK_TRIGGER_EVENTS = [
  {
    value: 'app_mention',
    label: 'Bot mentioned',
    description: 'Someone @-mentions the reef bot in a channel it is in.',
  },
  {
    value: 'reaction_added',
    label: 'Reaction added',
    description: 'An emoji reaction is added. Narrow it with an emoji filter.',
  },
  {
    value: 'slash_command',
    label: 'Slash command',
    description:
      '`/reef run <workflow>` names its workflow directly, so it starts that one rather than fanning out to subscribers.',
  },
] as const satisfies readonly TriggerEventOption[];

/**
 * A generic webhook has no vocabulary of its own — the sender decides what its
 * events are called. These are the two reef reads out of a delivery, offered as
 * a starting point; the builder also accepts anything typed in.
 */
export const WEBHOOK_TRIGGER_EVENT_HINTS = [
  {
    value: 'default',
    label: 'default',
    description: 'What a delivery that names no event is filed under.',
  },
  {
    value: 'created',
    label: 'created',
    description: 'A conventional name for a creation event.',
  },
  {
    value: 'updated',
    label: 'updated',
    description: 'A conventional name for an update event.',
  },
] as const satisfies readonly TriggerEventOption[];

/**
 * The bare values, as a tuple of string literals rather than `string[]`.
 *
 * The cast is what keeps `events: ['issue.created']` type-checked in an
 * authored workflow: a plain `.map()` widens to `string[]`, and a picklist
 * built from that accepts any string at compile time and only complains at
 * parse time — which is a worse place to find a typo.
 */
type EventValues<T extends readonly TriggerEventOption[]> = { [K in keyof T]: T[K]['value'] };

export const LINEAR_TRIGGER_EVENT_VALUES = LINEAR_TRIGGER_EVENTS.map(
  (event) => event.value,
) as unknown as EventValues<typeof LINEAR_TRIGGER_EVENTS>;

export const GITHUB_TRIGGER_EVENT_VALUES = GITHUB_TRIGGER_EVENTS.map(
  (event) => event.value,
) as unknown as EventValues<typeof GITHUB_TRIGGER_EVENTS>;

export const JIRA_TRIGGER_EVENT_VALUES = JIRA_TRIGGER_EVENTS.map(
  (event) => event.value,
) as unknown as EventValues<typeof JIRA_TRIGGER_EVENTS>;

export const SLACK_TRIGGER_EVENT_VALUES = SLACK_TRIGGER_EVENTS.map(
  (event) => event.value,
) as unknown as EventValues<typeof SLACK_TRIGGER_EVENTS>;

/** Sources that arrive as an event with a payload, keyed for the builder. */
export const TRIGGER_EVENT_CATALOG: Record<string, readonly TriggerEventOption[]> = {
  linear: LINEAR_TRIGGER_EVENTS,
  github: GITHUB_TRIGGER_EVENTS,
  jira: JIRA_TRIGGER_EVENTS,
  slack: SLACK_TRIGGER_EVENTS,
  webhook: WEBHOOK_TRIGGER_EVENT_HINTS,
};

/**
 * The event name reef files a generic webhook delivery under when the sender
 * did not name one.
 */
export const DEFAULT_WEBHOOK_EVENT = 'default';
